import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TableCard from './TableCard';
import FloorPlanView from './FloorPlanView';
import WaiterSummaryBar from './WaiterSummaryBar';
import './TableGrid.css';

const SKELETON_COUNT = 8;

function TableCardSkeleton() {
  return (
    <div className="table-card-skeleton" aria-hidden="true">
      <div className="skeleton-line skeleton-line--header" />
      <div className="skeleton-line skeleton-line--badge" />
      <div className="skeleton-line skeleton-line--amount" />
    </div>
  );
}

function TableGrid({ tables, orders, alerts, groups, onTableClick, loading }) {
  const { t } = useTranslation();
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [layout, setLayout] = useState('grid');

  // Extract unique rooms from table locations
  const rooms = useMemo(() => {
    const locationSet = new Set();
    tables.forEach((table) => {
      if (table.location) {
        locationSet.add(table.location);
      }
    });
    return Array.from(locationSet).sort();
  }, [tables]);

  const hasRooms = rooms.length > 1 || (rooms.length === 1 && tables.some((t) => !t.location));
  const hasFloorPlan = tables.some((t) => t.positionX != null && t.positionY != null);

  // Filter tables by selected room
  const filteredTables = useMemo(() => {
    if (!selectedRoom) return tables;
    return tables.filter((table) => table.location === selectedRoom);
  }, [tables, selectedRoom]);

  // Sort tables by tableNumber
  const sortedTables = useMemo(() => {
    return [...filteredTables].sort((a, b) => {
      const numA = parseInt(a.tableNumber, 10) || 0;
      const numB = parseInt(b.tableNumber, 10) || 0;
      return numA - numB;
    });
  }, [filteredTables]);

  return (
    <div className="table-grid-container">
      <WaiterSummaryBar tables={filteredTables} orders={orders} />

      {(hasRooms || hasFloorPlan) && (
        <div className="room-tabs-row">
          {hasRooms && (
            <div className="room-tabs">
              <button
                className={`room-tab ${!selectedRoom ? 'active' : ''}`}
                onClick={() => setSelectedRoom(null)}
              >
                {t('waiter.allRooms')}
                <span className="room-tab-count">{tables.length}</span>
              </button>
              {rooms.map((room) => {
                const count = tables.filter((t) => t.location === room).length;
                return (
                  <button
                    key={room}
                    className={`room-tab ${selectedRoom === room ? 'active' : ''}`}
                    onClick={() => setSelectedRoom(room)}
                  >
                    {room}
                    <span className="room-tab-count">{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {hasFloorPlan && (
            <div className="layout-toggle">
              <button
                className={`layout-toggle-btn ${layout === 'grid' ? 'active' : ''}`}
                onClick={() => setLayout('grid')}
                title={t('waiter.gridView', 'Grilla')}
                aria-label={t('waiter.gridView', 'Grilla')}
              >
                <span className="material-icons" aria-hidden="true">grid_view</span>
              </button>
              <button
                className={`layout-toggle-btn ${layout === 'plan' ? 'active' : ''}`}
                onClick={() => setLayout('plan')}
                title={t('waiter.floorPlanView', 'Plano')}
                aria-label={t('waiter.floorPlanView', 'Plano')}
              >
                <span className="material-icons" aria-hidden="true">map</span>
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="table-grid" role="status" aria-label={t('common.loading')}>
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <TableCardSkeleton key={i} />
          ))}
        </div>
      ) : tables.length === 0 ? (
        <div className="table-grid-empty">
          <span className="table-grid-empty-icon">🍽️</span>
          <p>{t('waiter.noTables')}</p>
          <p className="table-grid-empty-hint">{t('waiter.noTablesHint')}</p>
        </div>
      ) : layout === 'plan' && hasFloorPlan ? (
        <div className="table-grid-plan-wrapper">
          <FloorPlanView
            tables={filteredTables}
            orders={orders}
            alerts={alerts}
            groups={groups}
            onTableClick={onTableClick}
          />
        </div>
      ) : (
        <div className="table-grid">
          {sortedTables.map((table) => (
            <TableCard
              key={table.tableId || table.tableNumber}
              table={table}
              orders={orders}
              alerts={alerts[String(table.tableNumber)]}
              onClick={onTableClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TableGrid;
